import { Component, ElementRef, AfterViewInit, input, viewChild, inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { noop } from 'rxjs';

import { ApRadioGroupComponent } from '../radio-group.component';

/**
 * @ngdoc component
 * @module ApRadioButtonModule
 * @name ApRadioButtonComponent
 * @description A component for a single radio button including a label.
 */
@Component({
    selector: 'ap-radio-button',
    templateUrl: './radio-button.component.html',
    styleUrls: ['./radio-button.component.scss']
})
export class ApRadioButtonComponent implements ControlValueAccessor, AfterViewInit {
    protected control = inject(NgControl, { self: true, optional: true })!;

    protected readonly input = viewChild('input', { read: ElementRef });

    /**
     * Required - HTML content to show behind radio button
     */
    public readonly label = input.required<string>();

    /**
     * Required - Value of radio button
     */
    public readonly value = input.required<any>();

    /**
     * Required - Name of radio group
     */
    public readonly name = input.required<string>();

    /**
     * HTML content to show as subtitle
     */
    public readonly subTitle = input<string>();

    /**
     * Id of element that describes radio button
     */
    public readonly ariaDescribedby = input<string>();

    /**
     * Optional radio-group reference
     */
    public readonly fieldset = input<ApRadioGroupComponent>();

    constructor() {
        this.control && (this.control.valueAccessor = this);
    }

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = noop;

    /** @internal */
    public isDisabled = false;

    /** @internal */
    public isChecked = false;

    /**
     * Check again if the value is the same as the formControl. Somehow the value was not set correctly after page load
     * The checked state was not set correctly in Firefox.
     */
    public ngAfterViewInit(): void {
        if (this.value() === this.control.value) {
            this.writeValue(this.control.value);
        }
    }

    /**
     * Set checked state if input changes
     */
    protected radioChange(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.onChange(value);
        this.onTouched();
    }

    /**
     * Set touched and dirty state if input blurs
     */
    protected radioBlur(): void {
        this.onTouched();
        if (this.control?.control) {
            this.control.control.markAsDirty();
        }
    }

    /**
     * Control value accessor function
     * Registers on change event
     * @internal
     */
    public registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Control value accessor function
     * Registers on touched event
     * @internal
     */
    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Control value accessor function
     * Sets isDisabled state
     * @internal
     */
    public setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    /**
     * Control value accessor function
     * Write value is called when value is set programmatically in FormControl
     * @internal
     */
    public writeValue(value: any) {
        this.isChecked = value === this.value();

        /**
         * Angular does not set the html :checked state of the radio button correctly
         * when updating it programmatically. Therefore we update it manually here.
         */
        const inputValue = this.input();
        if (inputValue) {
            inputValue.nativeElement.checked = this.isChecked;
        }
    }

    /**
     * Aria described by contains conditional fields that describe the input field
     */
    protected getAriaDescribedBy(): string {
        return [
            ...(this.ariaDescribedby() ? [this.ariaDescribedby()] : []),
            ...(this.fieldset()?.ariaDescribedBy ? [this.fieldset().ariaDescribedBy] : [])
        ].join(' ');
    }
}
